/**
 * Sensitive-keyword detector shared across compose surfaces.
 *
 * Posts that hit one of these keywords are routed to Coach Kiley's moderation
 * queue before they go live in the Sisterhood feed. Comments use the same
 * detector so a tender topic in a thread does not leak into the feed without
 * a coach's eyes on it. The list mirrors the Privacy Policy promise to keep
 * notification text general; this is a nudge, not an exhaustive filter.
 */

export const SENSITIVE_KEYWORDS = [
  // Pregnancy and loss
  "pregnancy",
  "pregnant",
  "miscarriage",
  "miscarried",
  "stillbirth",
  "loss",
  // Weight and numbers
  "weight",
  "lbs",
  "pounds",
  "kg",
  "bmi",
  // Cycle / clinical terms that a moderator should see first
  "cycle",
  "period",
  "diagnosis",
  "disorder",
  // Eating-disorder language
  "anorexia",
  "anorexic",
  "bulimia",
  "bulimic",
  "purge",
  "purging",
  "starve",
  "starving",
  // Self-harm
  "suicide",
  "self-harm",
  "self harm",
  "cutting",
  "kill myself",
  "end it all",
];

export interface SensitiveHit {
  keyword: string;
  matchIndex: number;
}

/** Return the first sensitive keyword found in `text`, or null. */
export function detectSensitive(text: string): string | null {
  if (!text) return null;
  const lower = text.toLowerCase();
  const hit = SENSITIVE_KEYWORDS.find((k) => lower.includes(k.toLowerCase()));
  return hit ?? null;
}

/** Return ALL sensitive keywords found, with their first-match index. */
export function detectAllSensitive(text: string): SensitiveHit[] {
  if (!text) return [];
  const lower = text.toLowerCase();
  const hits: SensitiveHit[] = [];
  for (const k of SENSITIVE_KEYWORDS) {
    const idx = lower.indexOf(k.toLowerCase());
    if (idx >= 0) hits.push({ keyword: k, matchIndex: idx });
  }
  return hits.sort((a, b) => a.matchIndex - b.matchIndex);
}

/** True when the text should be routed to a moderation queue. */
export function shouldQueueForReview(text: string): boolean {
  return detectSensitive(text) !== null;
}
